import { GROUP_LABEL_SEPARATOR } from "./select.constants";

/**
 * The selected values could be one, none, or multiple items since the component can be a single or multiple item
 * select. This function just normalises the values so they're always an array.
 * @param values Selected values.
 * @returns      Array of selected values.
 *
 * @example
 * // Returns ['a']
 * transformValues(['a']);
 *
 * @example
 * // Returns []
 * transformValues(null);
 *
 * // Returns ['a']
 * transformValues('a');
 */
export const transformValues = <IValue extends {}>(values: IValue | IValue[] | null) => {
    if (Array.isArray(values)) {
        return values;
    } else if (values === null || typeof values === "undefined") {
        return [];
    } else {
        return [values];
    }
};

/**
 * Whether an item matches a search, ignoring case. An item in a group matches on its group name too, so searching a
 * group finds all of its items.
 * @param label  The item's label.
 * @param group  The item's group name, if it has one.
 * @param search The text to look for.
 * @returns      True when the label, or the group name, contains the search.
 */
export const matchesGroupedSearch = (label: string, group: string | undefined, search: string) =>
    (group ? `${group}${GROUP_LABEL_SEPARATOR}${label}` : label).toLowerCase().includes(search.toLowerCase());

/**
 * Whether an item's label matches the search typed into a select, ignoring case and surrounding whitespace. Both the
 * options and the selected items are searched this way, so that the two columns of a multiple select always agree.
 * @param label  The item's label.
 * @param search The text typed into the search box.
 * @returns      True when the label contains the search.
 */
export const matchesSearch = (label: string, search: string) =>
    label.toLowerCase().trim().includes(search.toLowerCase().trim());

/**
 * The selected items whose labels match a search, or undefined when nothing is being searched. Selected items are always
 * held in full on the client, so they are searched here even when the options are searched somewhere else, e.g. by a
 * server.
 * @param selectedItems The items selected so far.
 * @param getLabel      Getter for an item's label.
 * @param search        The text typed into the search box.
 * @returns             The matching selected items, or undefined when the search is empty.
 */
export const getSelectedSearchMatches = <IItem>(
    selectedItems: IItem[],
    getLabel: (item: IItem) => string,
    search: string
) => (search.trim().length === 0 ? undefined : selectedItems.filter((item) => matchesSearch(getLabel(item), search)));
